
import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const { ref, isVisible } = useScrollAnimation<HTMLElement>({ threshold: 0.2 });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !consent) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('consent', 'yes');

      const response = await fetch('https://readdy.ai/api/form/d5v9pc1dkj8fk05388dg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setConsent(false);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      ref={ref}
      id="newsletter" 
      className="py-20 lg:py-32 bg-gradient-to-br from-[#D4AF37]/10 via-[#FFF9F5] to-[#8B2635]/5 relative overflow-hidden"
    >
      {/* Floating festive elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-10 left-[5%] text-5xl transition-all duration-1000 ${isVisible ? 'opacity-10 translate-y-0' : 'opacity-0 -translate-y-10'}`}>🎁</div>
        <div className={`absolute top-20 right-[10%] text-4xl animate-float transition-all duration-1000 delay-200 ${isVisible ? 'opacity-10' : 'opacity-0'}`}>✨</div>
        <div className={`absolute bottom-20 left-[15%] text-6xl animate-bounce-soft transition-all duration-1000 delay-300 ${isVisible ? 'opacity-10' : 'opacity-0'}`}>🎄</div>
        <div className={`absolute bottom-10 right-[20%] text-4xl animate-twinkle transition-all duration-1000 delay-400 ${isVisible ? 'opacity-10' : 'opacity-0'}`}>⭐</div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          className={`bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/10 rounded-[32px] p-12 lg:p-16 relative overflow-hidden border border-[#D4AF37]/30 shadow-2xl transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
          }`}
        >
          <div className="absolute top-8 right-8 text-6xl opacity-10 animate-bounce-soft">🎁</div>
          <div className="absolute bottom-8 left-8 text-6xl opacity-10 animate-float">🎄</div>
          
          <div className="relative z-10 text-center">
            <div 
              className={`w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#8B2635] to-[#A52A3A] rounded-full flex items-center justify-center transition-all duration-700 delay-200 hover:scale-110 hover:rotate-12 cursor-pointer ${
                isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
              }`}
            >
              <i className="ri-gift-fill text-4xl text-white animate-pulse"></i>
            </div>
            
            <h2 
              className={`text-4xl lg:text-5xl font-bold text-[#1B4332] mb-4 transition-all duration-700 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Get Daily Gift Ideas
            </h2>
            <p 
              className={`text-xl text-gray-700 mb-8 max-w-2xl mx-auto transition-all duration-700 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              Join 50,000+ smart shoppers receiving exclusive gift recommendations, early access to reviews, and special holiday deals straight to your inbox.
            </p>

            <form 
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit} 
              className={`max-w-xl mx-auto transition-all duration-700 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 px-6 py-4 rounded-full border-2 border-white bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#D4AF37] focus:shadow-lg focus:shadow-[#D4AF37]/20 transition-all duration-300 text-lg"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-gradient-to-r from-[#8B2635] to-[#A52A3A] text-white rounded-full font-semibold hover:shadow-xl hover:shadow-[#8B2635]/30 transition-all duration-300 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap btn-festive group"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Subscribing...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Subscribe Now
                      <i className="ri-send-plane-fill group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
                    </span>
                  )}
                </button>
              </div>

              <div className="flex items-start gap-3 justify-center mb-4">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  required
                  className="mt-1 w-5 h-5 text-[#8B2635] border-gray-300 rounded focus:ring-[#8B2635] cursor-pointer transition-transform hover:scale-110"
                />
                <label htmlFor="consent" className="text-sm text-gray-700 text-left cursor-pointer hover:text-gray-900 transition-colors">
                  I agree to receive gift recommendations and marketing emails. You can unsubscribe at any time. 
                  View our <a href="#" className="text-[#8B2635] underline hover:text-[#A52A3A] transition-colors">Privacy Policy</a>.
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-100 border border-green-300 rounded-xl text-green-800 font-medium animate-scale-in visible flex items-center justify-center gap-2">
                  <i className="ri-checkbox-circle-fill text-xl"></i>
                  🎉 Success! Check your email to confirm your subscription.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-100 border border-red-300 rounded-xl text-red-800 font-medium animate-scale-in visible flex items-center justify-center gap-2">
                  <i className="ri-error-warning-fill text-xl"></i>
                  ⚠️ Please check your email and consent checkbox.
                </div>
              )}
            </form>

            <div 
              className={`flex items-center justify-center gap-8 mt-8 text-sm text-gray-600 flex-wrap transition-all duration-700 delay-600 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {[
                { icon: 'ri-mail-check-fill', text: 'No spam, ever' },
                { icon: 'ri-shield-check-fill', text: 'Unsubscribe anytime' },
                { icon: 'ri-gift-fill', text: 'Exclusive deals' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 group cursor-pointer hover:text-[#1B4332] transition-colors"
                >
                  <i className={`${item.icon} text-[#1B4332] text-xl group-hover:scale-125 group-hover:rotate-12 transition-transform`}></i>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
